import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

interface Props {
  children?: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary] Caught component error:', error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="p-8 max-w-lg mx-auto my-12 text-center">
          <Card className="border-red-500/30 bg-red-500/5">
            <CardContent className="p-6 space-y-4">
              <div className="h-12 w-12 rounded-full bg-red-500/10 flex items-center justify-center mx-auto text-red-500">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <div>
                <h3 className="text-base font-bold text-foreground">Something went wrong</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  {this.state.error?.message || 'An unexpected rendering error occurred in this view.'}
                </p>
              </div>
              <div className="pt-2 flex justify-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={this.handleReset}
                  className="text-xs gap-1.5 border-red-500/30 text-red-600 hover:bg-red-500/10"
                >
                  <RefreshCw className="h-3.5 w-3.5" /> Reload Page
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
